import React, {useState, ChangeEvent, FormEvent} from 'react';
import {useLocation, useParams, Link} from 'react-router-dom';
import {AuthService} from '@/modules/auth/services/authService';
import {ThemedLogo} from '@/shared/themed/ThemedLogo';
import {PasswordInput} from './PasswordInput';

interface PasswordError {
    passwordLength?: boolean;
    passwordLetters?: boolean;
    passwordNumber?: boolean;
}

const ResetPasswordPage: React.FC = () => {
    const [state, setState] = useState<{[x: string]: string}>({});
    const [sent, setSent] = useState(false);
    const [expired, setExpired] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [error, setError] = useState<{[x: string]: string}>({});
    const [passwordError, setPasswordError] = useState<PasswordError>({});
    const {token} = useParams<{token: string}>();
    const [sending, setSending] = useState(false);
    const [resetting, setResetting] = useState(false);
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const ref = query.get('ref');

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;

        if (name === 'password1') {
            const passwordUppercaseReg = /[A-Z]/;
            const passwordLowercaseReg = /[a-z]/;
            const passwordNumberReg = /[\d]/;

            setPasswordError({
                passwordLetters: !passwordUppercaseReg.test(value) || !passwordLowercaseReg.test(value),
                passwordNumber: !passwordNumberReg.test(value),
                passwordLength: value.length < 9,
            });
        }

        setState((prevState) => ({...prevState, [name]: value}));
        setError((prevError) => ({...prevError, [name]: ''}));
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!state?.username) {
            setError({message: 'Please enter your email', username: 'true'});
            return;
        }

        setSending(true);

        try {
            await AuthService.requestPasswordReset(state.username);
            setSending(false);
            setError({message: '', username: ''});
            setSent(true);
        } catch (err: any) {
            setSending(false);
            const message = err.response?.data?.error || 'Failed to send reset email';
            setError({message: message, username: 'true'});
        }
    };

    const handleReset = async (event: FormEvent) => {
        event.preventDefault();
        if (!state?.password1) {
            setError({message: 'Please enter your new password', password1: 'true'});
            return;
        }
        if (!state?.password2) {
            setError({
                message: 'Please confirm your new password',
                password1: '',
                password2: 'true',
            });
            return;
        }
        for (const key in passwordError) {
            if (passwordError[key as keyof PasswordError]) {
                setError({
                    message: 'Please ensure password meets requirements',
                    password1: 'true',
                    password2: '',
                });
                return;
            }
        }
        if (state.password1 !== state.password2) {
            setError({
                message: 'Please ensure passwords match',
                password1: 'true',
                password2: 'true',
            });
            return;
        }
        if (!token) {
            return;
        }

        setResetting(true);

        try {
            await AuthService.resetPassword(token, state.password1);
            setResetting(false);
            setError({message: '', password1: '', password2: ''});
            setUpdated(true);
        } catch (err: any) {
            setResetting(false);
            const message = err.response?.data?.error || 'Failed to reset password';

            if (message === 'This link has expired') {
                setExpired(true);
                return;
            }

            setError({message: message});
        }
    };

    const getLoginLink = () => (ref ? `/login?ref=${ref}` : '/login');

    return (
        <div className="h-screen flex items-center justify-center bg-mld-background">
            <div className="w-full max-w-md mx-4">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to={getLoginLink()}>
                        <ThemedLogo className="mx-auto max-h-[200px] max-w-[300px] h-auto w-auto object-contain" />
                    </Link>
                </div>

                {/* Card Container */}
                <div className="bg-white rounded-2xl border border-mld-panel-border shadow-sm p-8">
                    {/* Email Request Form */}
                    {!sent && !token && (
                        <>
                            <h2 className="text-3xl font-light text-gray-800 mb-2">Forgot Password</h2>
                            <p className="text-gray-600 mb-6">
                                Enter the email address associated with your account and we&apos;ll send you an email
                                with a password reset link.
                            </p>

                            {error?.message && (
                                <div className="mb-4 p-3 bg-mld-danger/5 border border-mld-danger/20 rounded-lg">
                                    <p className="text-mld-danger text-sm">{error.message}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <input
                                        type="email"
                                        name="username"
                                        value={state?.username || ''}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border ${
                                            error?.username ? 'border-mld-danger' : 'border-gray-300'
                                        } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                        placeholder="Enter your email"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="w-full bg-mld-login-button hover:bg-opacity-90 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    {sending ? 'Sending...' : 'Reset Password'}
                                </button>

                                <div className="text-center">
                                    <Link
                                        to={getLoginLink()}
                                        className="text-mld-muted-text hover:text-mld-login-button text-sm transition-colors">
                                        Back to sign in
                                    </Link>
                                </div>
                            </form>
                        </>
                    )}

                    {/* Email Sent Success */}
                    {sent && !token && (
                        <div className="text-center">
                            <div className="mb-6">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full">
                                    <svg
                                        className="w-10 h-10 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-2">Check Your Email</h3>
                            <p className="text-gray-600 mb-6">
                                We&apos;ve sent an email with instructions and a link to reset your password
                            </p>
                            <Link
                                to={getLoginLink()}
                                className="text-mld-muted-text hover:text-mld-login-button transition-colors">
                                Back to sign in
                            </Link>
                        </div>
                    )}

                    {/* Password Reset Form */}
                    {token && !updated && !expired && (
                        <>
                            <h2 className="text-3xl font-light text-gray-800 mb-6">Create New Password</h2>

                            {error?.message && (
                                <div className="mb-4 p-3 bg-mld-danger/5 border border-mld-danger/20 rounded-lg">
                                    <p className="text-mld-danger text-sm">{error.message}</p>
                                </div>
                            )}

                            <form onSubmit={handleReset} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                                    <PasswordInput
                                        name="password1"
                                        value={state?.password1 || ''}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 pr-12 border ${
                                            error?.password1 ? 'border-mld-danger' : 'border-gray-300'
                                        } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                        placeholder="Enter new password"
                                        autoComplete="new-password"
                                    />
                                    <div className="mt-2 space-y-1 text-sm">
                                        <PasswordRequirement
                                            met={!passwordError?.passwordLength && !!state?.password1}
                                            text="At least 9 characters long"
                                        />
                                        <PasswordRequirement
                                            met={!passwordError?.passwordLetters && !!state?.password1}
                                            text="Include at least 1 uppercase and lowercase letter"
                                        />
                                        <PasswordRequirement
                                            met={!passwordError?.passwordNumber && !!state?.password1}
                                            text="Include at least 1 number"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm New Password
                                    </label>
                                    <PasswordInput
                                        name="password2"
                                        value={state?.password2 || ''}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 pr-12 border ${
                                            error?.password2 ? 'border-mld-danger' : 'border-gray-300'
                                        } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                        placeholder="Confirm new password"
                                        autoComplete="new-password"
                                    />
                                    <div className="mt-2 space-y-1 text-sm">
                                        <PasswordRequirement
                                            met={
                                                state?.password1 === state?.password2 &&
                                                !!state?.password1 &&
                                                !!state?.password2
                                            }
                                            text="Passwords match"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={resetting}
                                    className="w-full bg-mld-login-button hover:bg-opacity-90 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    {resetting ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </form>
                        </>
                    )}

                    {/* Expired Link */}
                    {expired && (
                        <div className="text-center">
                            <div className="mb-6">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full">
                                    <svg
                                        className="w-10 h-10 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-2">Sorry!</h3>
                            <p className="text-gray-600 mb-6">
                                This link has expired. If you still want to reset your password, please make another
                                request.
                            </p>
                            <Link
                                to={getLoginLink()}
                                className="text-mld-muted-text hover:text-mld-login-button transition-colors">
                                Back to sign in
                            </Link>
                        </div>
                    )}

                    {/* Success */}
                    {updated && (
                        <div className="text-center">
                            <div className="mb-6">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full">
                                    <svg
                                        className="w-10 h-10 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-2">All Done!</h3>
                            <p className="text-gray-600 mb-6">
                                Your password has been successfully reset. Please sign in using your new password.
                            </p>
                            <Link
                                to={getLoginLink()}
                                className="inline-block bg-mld-login-button hover:bg-opacity-90 text-white font-medium py-3 px-6 rounded-lg transition-colors">
                                Back to sign in
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Helper component for password requirements
const PasswordRequirement: React.FC<{met: boolean; text: string}> = ({met, text}) => {
    return (
        <div className="flex items-center gap-2">
            <div
                className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                    met ? 'bg-mld-primary' : 'bg-gray-300'
                }`}>
                {met && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                )}
            </div>
            <span className={met ? 'text-mld-success' : 'text-gray-500'}>{text}</span>
        </div>
    );
};

export default ResetPasswordPage;
